"use client";
import { useState } from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select } from "@/components/ui/select";
import useIsMobile from "@/hooks/use-is-mobile";
import {
  TABS,
  type ProfileTab,
  type ProfileTabDef,
} from "@/features/candidate/lib/profile-constants";
import { Icon } from "@/components/ui/icon";

export function ProfileTabs({
  activeTab,
  onTabChange,
  extraTabs,
}: {
  activeTab: ProfileTab;
  onTabChange: (tab: ProfileTab) => void;
  /**
   * Extra context-specific tabs (e.g. "Resume" on an imported candidate)
   * appended after the default tab list. Callers are responsible for only
   * rendering tabs whose content they'll handle.
   */
  extraTabs?: ProfileTabDef[];
}) {
  const tabs = extraTabs ? [...TABS, ...extraTabs] : TABS;
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Mobile: the horizontal strip scrolls sideways and isn't touch-friendly, so
  // collapse it into a single "☰ {current section}" button that opens a bottom
  // drawer of the sections (the shared select.tsx mobile Drawer). Desktop keeps
  // the strip below.
  if (isMobile) {
    const active = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
    return (
      <Select open={open} onOpenChange={setOpen} modal>
        <Select.Trigger
          active
          aria-label={`Section: ${active.label}`}
          className="w-full gap-2"
        >
          <Icon
            name="IconBarsThree"
            size={16}
            className="shrink-0 text-heading opacity-60"
          />
          <span className="flex-1 truncate text-left text-sm font-medium text-foreground">
            {active.label}
          </span>
          <Select.Icon />
        </Select.Trigger>
        <Select.Content>
          <Select.List>
            {tabs.map((tab) => {
              const selected = tab.id === activeTab;
              return (
                <Select.Item
                  key={tab.id}
                  selected={selected}
                  onSelect={() => {
                    onTabChange(tab.id);
                    setOpen(false);
                  }}
                >
                  <Select.ItemIcon>
                    <Icon name={tab.icon} className="text-muted-foreground size-3.5" />
                  </Select.ItemIcon>
                  <Select.ItemLabel selected={selected}>
                    {tab.label}
                  </Select.ItemLabel>
                  {selected && <Select.ItemCheck />}
                </Select.Item>
              );
            })}
          </Select.List>
        </Select.Content>
      </Select>
    );
  }

  return (
    <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <Tabs
        value={activeTab}
        onValueChange={(v) => onTabChange(v as ProfileTab)}
        size="default"
      >
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="shrink-0">
              <Icon name={tab.icon} className="relative text-foreground" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
